import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Lock,
  Eye,
  Database,
  Users,
  FileText,
  Globe,
  Bell,
  UserCheck,
  AlertCircle,
} from 'lucide-react';
import { usePrivacyPolicy } from '../../hooks/usePrivacyPolicy';
import { siteSettingsService } from '../../services/siteSettingsService';
import { useQuery } from '@tanstack/react-query';
import { MarketingPage } from '../../components/home/MarketingPageChrome';

const iconMap = {
  Shield,
  Lock,
  Eye,
  Database,
  Users,
  FileText,
  Globe,
  Bell,
  UserCheck,
  AlertCircle,
};

const LEGAL_NAV = [
  { to: '/terms', label: 'Terms of Service', active: false },
  { to: '/privacy', label: 'Privacy Policy', active: true },
];

const PrivacyPolicy = () => {
  const { data: policyResponse, isLoading, error } = usePrivacyPolicy();
  const policyData = policyResponse?.data;

  const { data: siteSettingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const siteSettings = siteSettingsResponse?.data || {};
  const contactEmail = siteSettings.site_email || 'privacy@vendalist.com';
  const contactPhone = siteSettings.site_phone || '1-800-VENDA-LIST';
  const contactAddress = siteSettings.site_address || 'VendaList Privacy Team, Accra, Ghana';

  const sections = useMemo(() => {
    if (!policyData?.sections) return [];
    return policyData.sections.map((section) => ({
      ...section,
      icon: iconMap[section.icon] || Shield,
    }));
  }, [policyData]);

  const settings = useMemo(() => policyData?.settings || {}, [policyData]);
  const lastUpdated = settings?.last_updated || 'November 2, 2025';

  return (
    <MarketingPage>
      <div className="border-b border-m-border-light bg-white">
        <nav className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <ul className="flex flex-wrap gap-x-6 gap-y-3">
            {LEGAL_NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`text-sm font-medium transition-colors ${
                    item.active
                      ? 'border-b-2 border-m-ink pb-1 text-m-ink'
                      : 'text-m-ink-muted hover:text-m-ink'
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {!isLoading && sections.length > 0 ? (
        <div className="sticky top-16 z-10 border-b border-m-border-light bg-white/95 backdrop-blur-sm">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
              {sections.map((section, index) => (
                <a
                  key={section.id || index}
                  href={`#section-${index}`}
                  className="shrink-0 px-3 py-1.5 text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <header className="mb-10">
          <h1 className="marketing-hero-title mb-3" style={{ color: '#0a0a0a' }}>
            Privacy Policy
          </h1>
          <p className="text-sm" style={{ color: '#6b6b6b' }}>
            Last updated: {lastUpdated}
          </p>
        </header>

        {settings?.introduction ? (
          <p
            className="mb-10 whitespace-pre-line text-[1.05rem] leading-relaxed"
            style={{ color: '#0a0a0a' }}
          >
            {settings.introduction}
          </p>
        ) : null}

        {isLoading ? (
          <div className="animate-pulse space-y-4 py-8">
            <div className="h-6 w-3/4 rounded bg-m-canvas-light" />
            <div className="h-4 rounded bg-m-canvas-light" />
            <div className="h-4 w-5/6 rounded bg-m-canvas-light" />
          </div>
        ) : null}

        {error && !isLoading ? (
          <p className="py-8 text-sm" style={{ color: '#6b6b6b' }}>
            Failed to load privacy policy. Please try again later.
          </p>
        ) : null}

        {!isLoading &&
          sections.map((section, index) => {
            const IconComponent = section.icon || Shield;
            return (
              <section
                key={section.id || index}
                id={`section-${index}`}
                className="scroll-mt-28 border-b border-m-border-light py-10 first:border-t first:border-m-border-light"
              >
                <div className="mb-4 flex items-center gap-3" style={{ color: '#0a0a0a' }}>
                  <IconComponent className="h-5 w-5" strokeWidth={1.5} />
                  <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-5">
                  {(section.content || []).map((item, iIndex) => (
                    <div key={iIndex}>
                      {item.subtitle ? (
                        <h3
                          className="mb-1.5 text-base font-bold tracking-tight"
                          style={{ color: '#0a0a0a' }}
                        >
                          {item.subtitle}
                        </h3>
                      ) : null}
                      <p className="text-[15px] leading-relaxed" style={{ color: '#0a0a0a' }}>
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

        {!isLoading && sections.length === 0 && !error ? (
          <p className="py-8 text-sm" style={{ color: '#6b6b6b' }}>
            No privacy policy sections available at this time.
          </p>
        ) : null}

        <footer className="mt-12 space-y-4 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
          <p>
            Your privacy matters to us. We’re committed to transparency and giving you control over
            your personal information.
          </p>
          <div className="space-y-1">
            {contactEmail ? <p>Email: {contactEmail}</p> : null}
            {contactPhone ? <p>Phone: {contactPhone}</p> : null}
            {contactAddress ? <p>Address: {contactAddress}</p> : null}
          </div>
          <p>
            <Link to="/contact" className="underline underline-offset-2 hover:text-m-ink">
              Contact support
            </Link>
            {' · '}
            <Link to="/terms" className="underline underline-offset-2 hover:text-m-ink">
              Terms of Service
            </Link>
          </p>
        </footer>
      </article>
    </MarketingPage>
  );
};

export default PrivacyPolicy;
